import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiEye, FiClock, FiCalendar, FiUser, FiStar, FiMapPin, FiDownload } = FiIcons;

const Birding = ({ standalone = false }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [activeGuide, setActiveGuide] = useState(0);

  // Endemic species data
  const endemicSpecies = [
    {
      name: 'Serendib Scops Owl',
      scientific: 'Otus thilohoffmanni',
      status: 'Critically Endangered',
      bestTime: 'Evening (18:00-20:00)',
      habitat: 'Dense canopy, lodge grounds',
      rarity: 5,
      recentSighting: '2024-02-14',
      image: 'https://images.unsplash.com/photo-1444927714506-8492d94b5ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    },
    {
      name: 'Sri Lankan Blue Magpie',
      scientific: 'Urocissa ornata',
      status: 'Endemic',
      bestTime: 'Early morning (06:00-09:00)',
      habitat: 'Mid-story, fruiting trees',
      rarity: 4,
      recentSighting: '2024-02-15',
      image: 'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    },
    {
      name: 'Red-faced Malkoha',
      scientific: 'Phaenicophaeus pyrrhocephalus',
      status: 'Endemic',
      bestTime: 'Morning (06:00-10:00)',
      habitat: 'Dense forest canopy',
      rarity: 4,
      recentSighting: '2024-02-16',
      image: 'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    },
    {
      name: 'Sri Lanka Frogmouth',
      scientific: 'Batrachostomus moniliger',
      status: 'Endemic',
      bestTime: 'Night (20:00-22:00)',
      habitat: 'Forest edges, clearings',
      rarity: 5,
      recentSighting: '2024-02-19',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
    }
  ];

  // Guide profiles
  const guides = [
    {
      name: 'Kapila Wickramasinghe',
      experience: '15 years',
      specialty: 'Endemic Owls & Night Birds',
      languages: ['English', 'Sinhala', 'German'],
      rating: 4.9,
      tours: 847,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'Kapila is renowned for his exceptional ability to locate the elusive Serendib Scops Owl. His intimate knowledge of nocturnal bird behavior has made him the go-to guide for serious listers.'
    },
    {
      name: 'Ranjith Perera',
      experience: '12 years',
      specialty: 'Photography & Behavioral Study',
      languages: ['English', 'Sinhala', 'Tamil'],
      rating: 4.8,
      tours: 623,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'A former wildlife photographer turned guide, Ranjith understands the technical needs of photographers and can position clients for the perfect shot while respecting bird behavior.'
    },
    {
      name: 'Samantha Silva',
      experience: '8 years',
      specialty: 'Forest Ecology & Conservation',
      languages: ['English', 'Sinhala', 'French'],
      rating: 4.7,
      tours: 445,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'Samantha combines birding expertise with deep ecological knowledge, providing guests with comprehensive understanding of rainforest ecosystems and conservation challenges.'
    }
  ];

  // Monthly birding calendar
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthlyData = {
    0: { // January
      weather: 'Dry season, excellent visibility',
      species: 28,
      highlights: ['Peak owl activity', 'Dry forest trails'],
      conditions: 'Excellent'
    },
    1: { // February
      weather: 'Prime birding season',
      species: 31,
      highlights: ['All endemics active', 'Perfect photography conditions'],
      conditions: 'Excellent'
    },
    2: { // March
      weather: 'Transition period, still good',
      species: 29,
      highlights: ['Breeding displays begin', 'Mixed flocks active'],
      conditions: 'Very Good'
    },
    3: { // April
      weather: 'Inter-monsoon, variable',
      species: 26,
      highlights: ['Nesting activity peaks', 'Early morning best'],
      conditions: 'Good'
    },
    4: { // May
      weather: 'Southwest monsoon begins',
      species: 24,
      highlights: ['Wet forest birding', 'Fewer tourists'],
      conditions: 'Fair'
    },
    5: { // June
      weather: 'Monsoon season',
      species: 22,
      highlights: ['Rainforest atmosphere', 'Dedicated birders only'],
      conditions: 'Challenging'
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <SafeIcon 
        key={i} 
        icon={FiStar} 
        className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-400'}`} 
      />
    ));
  };

  const renderRarityStars = (rarity) => {
    return Array.from({ length: 5 }, (_, i) => (
      <SafeIcon 
        key={i} 
        icon={FiStar} 
        className={`w-3 h-3 ${i < rarity ? 'text-red-400 fill-current' : 'text-gray-400'}`} 
      />
    ));
  };

  return (
    <section className={`${standalone ? 'pt-32' : 'py-20'} relative overflow-hidden`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="liquid-blob"></div>
        <div className="liquid-blob"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 glass px-6 py-3 rounded-full mb-6">
            <SafeIcon icon={FiEye} className="w-5 h-5 text-green-400" />
            <span className="text-white font-medium">Birding Sinharaja</span>
          </div>
          
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white mb-6">
            Endemic Species
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">
              Field Guide
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Access the most comprehensive birding resource for Sinharaja Forest Reserve. 
            Real-time sightings, expert guides, and detailed species information.
          </p>
        </motion.div>

        {/* Endemic Species Checklist */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-20"
        >
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-display text-3xl font-bold text-white">
              Endemic Species Checklist
            </h3>
            <button className="flex items-center space-x-2 btn-primary px-6 py-3">
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {endemicSpecies.map((species, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass p-6 rounded-2xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <img
                    src={species.image}
                    alt={species.name}
                    className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-bold text-white text-lg">{species.name}</h4>
                        <p className="text-gray-300 text-sm italic">{species.scientific}</p>
                      </div>
                      <div className="flex items-center space-x-1">
                        {renderRarityStars(species.rarity)}
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiClock} className="w-4 h-4 text-green-400" />
                        <span className="text-gray-300">{species.bestTime}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4 text-green-400" />
                        <span className="text-gray-300">{species.habitat}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 text-green-400" />
                        <span className="text-gray-300">
                          Last seen: {format(new Date(species.recentSighting), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        species.status === 'Critically Endangered' 
                          ? 'bg-red-500/20 text-red-300' 
                          : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {species.status}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Expert Guides */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-20"
        >
          <h3 className="font-display text-3xl font-bold text-white text-center mb-12">
            Expert Naturalist Guides
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {guides.map((guide, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`glass-strong rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
                  activeGuide === index ? 'ring-2 ring-green-400' : 'hover:glass'
                }`}
                onClick={() => setActiveGuide(index)}
              >
                <div className="relative">
                  <img
                    src={guide.image}
                    alt={guide.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4 glass px-3 py-1 rounded-full flex items-center space-x-1">
                    {renderStars(guide.rating)}
                    <span className="text-white text-sm ml-2">{guide.rating}</span>
                  </div>
                </div>

                <div className="p-6">
                  <h4 className="font-display text-xl font-bold text-white mb-2">
                    {guide.name}
                  </h4>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiUser} className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">{guide.experience} experience</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiEye} className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">{guide.specialty}</span>
                    </div>
                    <div className="text-sm text-gray-400">
                      {guide.tours} tours completed
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {guide.languages.map((lang, langIndex) => (
                      <span
                        key={langIndex}
                        className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>

                  <AnimatePresence>
                    {activeGuide === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-gray-300 text-sm border-t border-white/20 pt-4"
                      >
                        {guide.bio}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Seasonal Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="glass-strong rounded-2xl p-8"
        >
          <h3 className="font-display text-3xl font-bold text-white text-center mb-8">
            Birding Season Calendar
          </h3>

          {/* Month Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {months.slice(0, 6).map((month, index) => (
              <button
                key={index}
                onClick={() => setSelectedMonth(index)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  selectedMonth === index
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                    : 'glass text-white hover:bg-white/20'
                }`}
              >
                {month}
              </button>
            ))}
          </div>

          {/* Month Details */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedMonth}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              <div className="text-center">
                <h4 className="font-bold text-white mb-2">Weather</h4>
                <p className="text-gray-300 text-sm">
                  {monthlyData[selectedMonth]?.weather || 'Data not available'}
                </p>
              </div>

              <div className="text-center">
                <h4 className="font-bold text-white mb-2">Species Count</h4>
                <div className="text-3xl font-bold text-green-400 mb-2">
                  {monthlyData[selectedMonth]?.species || 0}
                </div>
                <p className="text-gray-400 text-sm">Average sightings</p>
              </div>

              <div className="text-center">
                <h4 className="font-bold text-white mb-2">Conditions</h4>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  monthlyData[selectedMonth]?.conditions === 'Excellent' ? 'bg-green-500/20 text-green-300' :
                  monthlyData[selectedMonth]?.conditions === 'Very Good' ? 'bg-blue-500/20 text-blue-300' :
                  monthlyData[selectedMonth]?.conditions === 'Good' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {monthlyData[selectedMonth]?.conditions || 'Unknown'}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 text-center">
            <p className="text-gray-300 mb-6">
              Ready to plan your birding adventure? Our concierge team is standing by 
              to help you optimize your visit timing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+94717714922"
                className="btn-primary px-6 py-3"
              >
                Speak with Birding Expert
              </a>
              <button className="glass px-6 py-3 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300">
                Request Custom Itinerary
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Birding;